import { useCallback, useState } from "react";
import { useMutation, useQuery } from "urql";
import {
  BulkCreateCandidatesMutation,
  BulkSendInvitationMutation,
  CandidatesQuery,
  DeleteCandidateMutation,
  SendInvitationMutation,
  UpdateCandidateMutation,
} from "../graphql/candidates.graphql";
import type { BulkSendInvitationMutation as BulkSendInvitationMutationResult } from "../graphql/generated/graphql";

export interface SponsorEmployee {
  sponsorEmployeeId: string;
  name: string | null;
  email: string | null;
}

export interface Candidate {
  candidateId: string;
  termId: string;
  sponsorEmployeeId: string;
  sponsorEmployee: SponsorEmployee | null;
  uniqueToken: string;
  status: string;
  isKeyCandidate: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface BulkCreateCandidatesInput {
  termId: string;
  emails: string[];
}

export interface UpdateCandidateInput {
  candidateId: string;
  isKeyCandidate: boolean;
}

export interface BulkCreateCandidateResult {
  email: string;
  matched: boolean;
  candidate: Candidate | null;
}

export interface BulkCreateCandidatesResult {
  results: BulkCreateCandidateResult[];
  createdCount: number;
  unmatchedEmails: string[];
}

export function useCandidates(termId: string | null) {
  const [result, reexecute] = useQuery({
    query: CandidatesQuery,
    variables: { termId: termId ?? "" },
    pause: !termId,
  });

  return {
    candidates: result.data?.candidates.nodes ?? [],
    loading: result.fetching,
    error: result.error?.message ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

/** 一括送信の結果。スキーマとずれないよう生成型から導出する */
export type BulkSendInvitationResult = NonNullable<
  BulkSendInvitationMutationResult["bulkSendInvitation"]
>;

export function useCandidateMutations() {
  const [, executeBulkCreateCandidates] = useMutation(
    BulkCreateCandidatesMutation,
  );
  const [, executeDeleteCandidate] = useMutation(DeleteCandidateMutation);
  const [, executeSendInvitation] = useMutation(SendInvitationMutation);
  const [, executeBulkSendInvitation] = useMutation(BulkSendInvitationMutation);
  const [, executeUpdateCandidate] = useMutation(UpdateCandidateMutation);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bulkCreateCandidates = useCallback(
    async (
      data: BulkCreateCandidatesInput,
    ): Promise<BulkCreateCandidatesResult | null> => {
      setLoading(true);
      setError(null);

      try {
        const result = await executeBulkCreateCandidates({ input: data });
        if (result.error) throw new Error(result.error.message);

        const bulkResult = result.data?.bulkCreateCandidates;
        if (!bulkResult) return null;

        return {
          results: bulkResult.results.map((r) => ({
            email: r.email,
            matched: r.matched,
            candidate: r.candidate
              ? {
                  candidateId: r.candidate.candidateId,
                  termId: r.candidate.termId,
                  sponsorEmployeeId: r.candidate.sponsorEmployeeId,
                  sponsorEmployee: r.candidate.sponsorEmployee ?? null,
                  // BulkCreate mutation の戻り値には uniqueToken を含めていないので空文字で埋める。
                  // candidate 一覧で再取得すると正しい値が入る。
                  uniqueToken: "",
                  status: r.candidate.status,
                  isKeyCandidate: r.candidate.isKeyCandidate,
                  createdAt: r.candidate.createdAt,
                  updatedAt: r.candidate.updatedAt,
                }
              : null,
          })),
          createdCount: bulkResult.createdCount,
          unmatchedEmails: bulkResult.unmatchedEmails,
        };
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "候補者の一括作成に失敗しました",
        );
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeBulkCreateCandidates],
  );

  const deleteCandidate = useCallback(
    async (candidateId: string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await executeDeleteCandidate({ candidateId });
        if (result.error) throw new Error(result.error.message);
        return result.data?.deleteCandidate ?? false;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "候補者の削除に失敗しました",
        );
        return false;
      } finally {
        setLoading(false);
      }
    },
    [executeDeleteCandidate],
  );

  const sendInvitation = useCallback(
    async (
      candidateId: string,
    ): Promise<{
      success: boolean;
      candidateId: string;
      errorMessage: string | null;
    } | null> => {
      setLoading(true);
      setError(null);

      try {
        const result = await executeSendInvitation({ candidateId });
        if (result.error) throw new Error(result.error.message);
        return result.data?.sendInvitation ?? null;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "招待メールの送信に失敗しました",
        );
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeSendInvitation],
  );

  const bulkSendInvitation = useCallback(
    async (termId: string): Promise<BulkSendInvitationResult | null> => {
      setLoading(true);
      setError(null);

      try {
        const result = await executeBulkSendInvitation({ termId });
        if (result.error) throw new Error(result.error.message);
        return result.data?.bulkSendInvitation ?? null;
      } catch (err) {
        setError(err instanceof Error ? err.message : "一括送信に失敗しました");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeBulkSendInvitation],
  );

  const updateCandidate = useCallback(
    async (data: UpdateCandidateInput): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await executeUpdateCandidate({ input: data });
        if (result.error) throw new Error(result.error.message);
        return Boolean(result.data?.updateCandidate);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "候補者の更新に失敗しました",
        );
        return false;
      } finally {
        setLoading(false);
      }
    },
    [executeUpdateCandidate],
  );

  return {
    bulkCreateCandidates,
    updateCandidate,
    deleteCandidate,
    sendInvitation,
    bulkSendInvitation,
    loading,
    error,
  };
}
